/**
 * AddToStorylineModal — link the current asset to one or more storylines from its
 * OWN industry (Figma "Add to Storyline", node 3246:44557-adjacent). Opened by the
 * "Add to Storyline" button on the Asset detail header.
 *
 * Fields: Industry (read-only — an asset belongs to exactly one industry, and a
 * storyline link is only valid when asset.industry == storyline.industry, backend
 * -enforced) + Storyline Name (Multiselect of that industry's storylines).
 *
 * The multiselect is pre-filled with the asset's CURRENT storyline memberships
 * (detail.storylineLinks), so Confirm applies the DIFF: newly-checked → LinkStorylineAsset,
 * unchecked → UnlinkStorylineAsset. Both link ops are idempotent. On success onSaved()
 * lets the page reload GetAsset so the header + tree reflect the new links.
 *
 * Only mounted for industry-anchored assets — the button is disabled (and this modal
 * never opens) for Topic-anchored / null-industry assets, since a storyline is a
 * per-industry concept.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  Spinner,
  type MultiselectProps,
} from '@amzn/awsui-components-react';
import { storylineClient } from '../../../client/storylineClient';
import type { StorylineSummary } from '../../../client/IvtHubClient';
import { denialHint } from '../../../utils/authorization';

export interface AddToStorylineModalProps {
  assetId: string;
  /** The asset's industry (guaranteed non-empty — button is disabled otherwise). */
  industry: string;
  /** The asset's current storyline links (for pre-selecting the multiselect). */
  currentStorylineIds: string[];
  onClose: () => void;
  /** Called after links are applied so the page can reload GetAsset. */
  onSaved: () => void;
}

export default function AddToStorylineModal({
  assetId,
  industry,
  currentStorylineIds,
  onClose,
  onSaved,
}: AddToStorylineModalProps) {
  const [storylines, setStorylines] = useState<StorylineSummary[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>(currentStorylineIds);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the industry's storylines (the multiselect options). Scoped server-side to
  // the asset's industry so cross-industry storylines never appear.
  useEffect(() => {
    let cancelled = false;
    storylineClient
      .list(industry)
      .then((items) => {
        if (!cancelled) setStorylines(items);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [industry]);

  const options: MultiselectProps.Option[] = useMemo(
    () => (storylines ?? []).map((s) => ({ value: s.id as string, label: s.name ?? (s.id as string) })),
    [storylines],
  );
  const selectedOptions = useMemo(
    () => options.filter((o) => o.value && selectedIds.includes(o.value)),
    [options, selectedIds],
  );

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    // Diff current memberships vs the new selection → link added, unlink removed.
    const current = new Set(currentStorylineIds);
    const next = new Set(selectedIds);
    const toLink = selectedIds.filter((id) => !current.has(id));
    const toUnlink = currentStorylineIds.filter((id) => !next.has(id));
    try {
      await Promise.all([
        ...toLink.map((id) => storylineClient.linkAsset(assetId, id)),
        ...toUnlink.map((id) => storylineClient.unlinkAsset(assetId, id)),
      ]);
      onSaved();
      onClose();
    } catch (e) {
      setError(denialHint(e) ?? '保存 Storyline 关联失败，请重试。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible
      onDismiss={() => !submitting && onClose()}
      size="medium"
      header="Add to Storyline"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" disabled={submitting} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              disabled={loadError || storylines === null}
              onClick={() => void handleConfirm()}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        <FormField label="Industry">
          {/* Read-only: a storyline link is only valid within the asset's own industry. */}
          <Select selectedOption={{ value: industry, label: industry }} options={[]} onChange={() => {}} disabled />
        </FormField>
        <FormField
          label="Storyline Name"
          // When the asset is already in some storylines they show pre-selected as
          // tokens; this line tells the user those are current memberships and that
          // deselecting removes the link (Confirm applies the add/remove diff).
          description={
            currentStorylineIds.length > 0
              ? `已关联 ${currentStorylineIds.length} 个 · 取消勾选即解除关联`
              : undefined
          }
        >
          {loadError ? (
            <Alert type="error">无法加载该行业的 Storyline，请重试。</Alert>
          ) : storylines === null ? (
            <Spinner />
          ) : (
            <Multiselect
              selectedOptions={selectedOptions}
              onChange={({ detail }) =>
                setSelectedIds(detail.selectedOptions.map((o) => o.value).filter((v): v is string => !!v))
              }
              options={options}
              placeholder="Choose an option"
              disabled={submitting}
              filteringType="auto"
              empty="该行业下暂无 Storyline"
            />
          )}
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
